/**
 * Piktogramm-Register des Mängelkatalogs („Standardmangel per Torte“, Paket B).
 *
 * Plan: docs/mangelkatalog-plan.md · Vertrag: docs/mangelkatalog-vertrag.md
 * (Abschnitt „Paket B“, Liste „Piktogramm-Schlüssel“ — bindend).
 *
 * Jeder Schlüssel zeigt entweder ein ISO-7010-Zeichen (SVG aus piktoSvgs.ts)
 * oder, wo es kein passendes Zeichen gibt, ein Lucide-Symbol mit Farbe. Eigene
 * Illustrationen gibt es bewusst nicht (Entscheid des Betreibers 27.09.).
 *
 * Farben der Lucide-Symbole (warme, gedeckte Töne): neutral `text-stone-700`,
 * Warnthemen `text-amber-700`, Brand/Heißarbeiten `text-red-700`, Gebote
 * `text-blue-700`, Erste Hilfe `text-emerald-700`, Wasser `text-sky-700`.
 */

import type { LucideIcon } from 'lucide-react';
import {
    ClipboardCheck,
    ClipboardList,
    CloudFog,
    Cog,
    Construction,
    Cross,
    Cylinder,
    FileText,
    Flame,
    Footprints,
    Forklift,
    Glasses,
    Grid3x3,
    HardHat,
    House,
    Lightbulb,
    ShieldAlert,
    Shovel,
    Slice,
    SquareDashed,
    TrainTrack,
    Truck,
    Waves,
    Wrench,
} from 'lucide-react';
import {
    SVG_E001,
    SVG_F001,
    SVG_M003,
    SVG_P001,
    SVG_P002,
    SVG_P003,
    SVG_P023,
    SVG_W001,
    SVG_W002,
    SVG_W003,
    SVG_W007,
    SVG_W008,
    SVG_W012,
    SVG_W015,
    SVG_W016,
    SVG_W017,
    SVG_W019,
    SVG_W020,
    SVG_W021,
    SVG_W035,
    SVG_W041,
    SVG_W042,
    SVG_W071,
} from './piktoSvgs';

export type PiktoDef =
    | { art: 'iso'; code: string; titel: string; svg: string }
    | { art: 'lucide'; icon: LucideIcon; titel: string; farbe: string; drehung?: number };

/** Reihenfolge wie im Vertrag. */
const SCHLUESSEL = [
    'allgemein', 'absturz', 'herabfallend', 'schwebende-last', 'elektrik', 'lichtbogen', 'brand', 'feuerloescher',
    'entzuendlich', 'explosiv', 'giftig', 'atemschutz', 'heiss', 'quetschen', 'stolpern', 'kopfstoss', 'gehoerschutz',
    'augenschutz', 'helm', 'notausgang', 'verbot', 'rauchverbot', 'feuerverbot', 'nicht-verstellen', 'strahlung', 'psa',
    'erste-hilfe', 'leiter', 'geruest', 'kran', 'baugrube', 'maschine', 'fahrzeug', 'stapler', 'verkehrsweg', 'absperrung',
    'organisation', 'dokument', 'pruefung', 'staub', 'asbest', 'wasser', 'gas', 'schweissen', 'beleuchtung', 'werkzeug',
    'saege', 'dach', 'oeffnung', 'laerm', 'sonderarbeit',
] as const;

type Schluessel = (typeof SCHLUESSEL)[number];

/** Alle gültigen Piktogramm-Schlüssel (Paket C und der Admin nutzen nur diese). */
export const PIKTO_SCHLUESSEL: readonly string[] = Object.freeze([...SCHLUESSEL]);

function iso(code: string, titel: string, svg: string): PiktoDef {
    const def: PiktoDef = { art: 'iso', code, titel, svg };
    return Object.freeze(def);
}

function lucide(icon: LucideIcon, titel: string, farbe: string, drehung?: number): PiktoDef {
    const def: PiktoDef = drehung === undefined
        ? { art: 'lucide', icon, titel, farbe }
        : { art: 'lucide', icon, titel, farbe, drehung };
    return Object.freeze(def);
}

// Zeichen, die mehrere Schlüssel teilen, nur einmal anlegen.
const W015 = iso('W015', 'Warnung vor schwebender Last', SVG_W015);
const F001 = iso('F001', 'Feuerlöscher', SVG_F001);
const M003 = iso('M003', 'Gehörschutz benutzen', SVG_M003);

// Der Record-Typ erzwingt: jeder Schlüssel hat genau eine Definition.
const DEFINITIONEN: Readonly<Record<Schluessel, PiktoDef>> = {
    allgemein: iso('W001', 'Allgemeines Warnzeichen', SVG_W001),
    absturz: iso('W008', 'Warnung vor Absturzgefahr', SVG_W008),
    herabfallend: iso('W035', 'Warnung vor herabfallenden Gegenständen', SVG_W035),
    'schwebende-last': W015,
    elektrik: iso('W012', 'Warnung vor elektrischer Spannung', SVG_W012),
    lichtbogen: iso('W042', 'Warnung vor Lichtbogen', SVG_W042),
    brand: F001,
    feuerloescher: F001,
    entzuendlich: iso('W021', 'Warnung vor feuergefährlichen Stoffen', SVG_W021),
    explosiv: iso('W002', 'Warnung vor explosionsgefährlichen Stoffen', SVG_W002),
    giftig: iso('W016', 'Warnung vor giftigen Stoffen', SVG_W016),
    atemschutz: iso('W041', 'Warnung vor Erstickungsgefahr', SVG_W041),
    heiss: iso('W017', 'Warnung vor heißer Oberfläche', SVG_W017),
    // W019 zeigt den Menschen zwischen zwei Wänden — „Quetschgefahr“ im Wortlaut der Norm.
    // (W024 „Warnung vor Handverletzungen“ passt nur für Hände an Maschinen.)
    quetschen: iso('W019', 'Warnung vor Quetschgefahr', SVG_W019),
    stolpern: iso('W007', 'Warnung vor Hindernissen am Boden', SVG_W007),
    kopfstoss: iso('W020', 'Warnung vor Hindernissen im Kopfbereich', SVG_W020),
    gehoerschutz: M003,
    augenschutz: lucide(Glasses, 'Augenschutz benutzen', 'text-blue-700'),
    helm: lucide(HardHat, 'Kopfschutz benutzen', 'text-blue-700'),
    notausgang: iso('E001', 'Notausgang (links)', SVG_E001),
    verbot: iso('P001', 'Allgemeines Verbotszeichen', SVG_P001),
    rauchverbot: iso('P002', 'Rauchen verboten', SVG_P002),
    feuerverbot: iso('P003', 'Keine offene Flamme; Feuer, offene Zündquelle und Rauchen verboten', SVG_P003),
    'nicht-verstellen': iso('P023', 'Abstellen oder Lagern verboten', SVG_P023),
    strahlung: iso('W003', 'Warnung vor radioaktiven Stoffen oder ionisierender Strahlung', SVG_W003),
    psa: lucide(HardHat, 'Persönliche Schutzausrüstung', 'text-blue-700'),
    'erste-hilfe': lucide(Cross, 'Erste Hilfe', 'text-emerald-700'),
    // Lucide hat keine Leiter: das Gleis, um 45° gedreht, steht senkrecht — zwei Holme mit Sprossen.
    leiter: lucide(TrainTrack, 'Leiter', 'text-stone-700', -45),
    geruest: lucide(Grid3x3, 'Gerüst', 'text-stone-700'),
    // Lucide hat keinen Kran; das ISO-Zeichen „schwebende Last“ (Haken mit Last) ist das bekannteste Bild dafür.
    kran: W015,
    baugrube: lucide(Shovel, 'Baugrube / Graben', 'text-stone-700'),
    maschine: lucide(Cog, 'Maschine', 'text-stone-700'),
    fahrzeug: lucide(Truck, 'Fahrzeug', 'text-stone-700'),
    stapler: lucide(Forklift, 'Stapler', 'text-stone-700'),
    verkehrsweg: lucide(Footprints, 'Verkehrsweg', 'text-stone-700'),
    absperrung: lucide(Construction, 'Absperrung', 'text-stone-700'),
    organisation: lucide(ClipboardList, 'Organisation', 'text-stone-700'),
    dokument: lucide(FileText, 'Dokument', 'text-stone-700'),
    pruefung: lucide(ClipboardCheck, 'Prüfung', 'text-stone-700'),
    staub: lucide(CloudFog, 'Staub', 'text-amber-700'),
    // Asbest ist krebserzeugend: W071 (Gesundheitsgefahr) statt Biogefährdung.
    asbest: iso('W071', 'Warnung vor gesundheitsgefährdenden Stoffen oder Gemischen', SVG_W071),
    wasser: lucide(Waves, 'Wasser', 'text-sky-700'),
    gas: lucide(Cylinder, 'Gas / Gasflasche', 'text-amber-700'),
    schweissen: lucide(Flame, 'Schweißen / Heißarbeiten', 'text-red-700'),
    beleuchtung: lucide(Lightbulb, 'Beleuchtung', 'text-stone-700'),
    werkzeug: lucide(Wrench, 'Werkzeug', 'text-stone-700'),
    saege: lucide(Slice, 'Säge / Schneiden', 'text-stone-700'),
    dach: lucide(House, 'Dach', 'text-stone-700'),
    oeffnung: lucide(SquareDashed, 'Öffnung', 'text-amber-700'),
    laerm: M003,
    sonderarbeit: lucide(ShieldAlert, 'Sonderarbeiten', 'text-stone-700'),
};

const REGISTER: ReadonlyMap<string, PiktoDef> = new Map(SCHLUESSEL.map((s) => [s, DEFINITIONEN[s]]));

/** Definition zu einem Schlüssel; null, leer oder unbekannt → 'allgemein' (W001). */
export function piktoAufloesen(schluessel: string | null | undefined): PiktoDef {
    if (typeof schluessel !== 'string') return DEFINITIONEN.allgemein;
    return REGISTER.get(schluessel) ?? DEFINITIONEN.allgemein;
}
